import { apiClient } from '../client';
import type { ApiResponse } from '../types';

// ---------------------------------------------------------------------------
// A tow provider's own equipment.
//
// Distinct from Units and Trailers, which describe a *customer's* fleet — the
// 18-wheelers being towed. This is the wrecker doing the towing, and it is the
// first record in the product of what a provider can physically do.
// ---------------------------------------------------------------------------

/**
 * The vocabulary, grouped the way an operator thinks about it. "Wrecker"
 * implies a boom; a rollback or wheel-lift is a truck without one, which is
 * why they are separate entries rather than sizes of the same thing.
 */
export const EQUIPMENT_GROUPS = [
  {
    key: 'trucks',
    types: [
      'LIGHT_DUTY_WRECKER',
      'MEDIUM_DUTY_WRECKER',
      'HEAVY_DUTY_WRECKER',
      'ROTATOR',
      'INTEGRATED_WRECKER',
      'ROLLBACK_FLATBED',
      'HEAVY_FLATBED',
      'WHEEL_LIFT',
      'SERVICE_TRUCK',
    ],
  },
  {
    key: 'trailers',
    types: ['LANDOLL_TRAILER', 'LOWBOY_TRAILER', 'DECK_TRAILER'],
  },
  {
    key: 'recovery',
    types: [
      'AIR_CUSHIONS',
      'ROTATING_DOLLIES',
      'RECOVERY_DOLLIES',
      'PORTABLE_WINCH',
      'FORKLIFT',
      'SKID_STEER',
      'CARGO_TRANSFER',
    ],
  },
  /**
   * What a roadside call needs, as opposed to a tow.
   *
   * The list above is all recovery gear — wreckers, dollies, air cushions —
   * which is right for dragging a truck out of a ditch and describes none of
   * the kit that fixes one where it stands. A provider who runs a mobile tire
   * rig or a welding truck had nothing to declare, so dispatch could not tell
   * who to send for a blown drive tire versus who to send for a winch-out.
   */
  {
    key: 'roadside',
    types: [
      'MOBILE_TIRE_RIG',
      'AIR_COMPRESSOR',
      'MOBILE_WELDER',
      'FUEL_DELIVERY_TANK',
      'JUMP_PACK',
      'REEFER_SERVICE_UNIT',
    ],
  },
  { key: 'other', types: ['OTHER'] },
] as const;

export const EQUIPMENT_TYPES = EQUIPMENT_GROUPS.flatMap((g) => [...g.types]);

/** Types where a tonnage rating is meaningful — a dolly has no capacity class. */
export const CAPACITY_TYPES = [
  'MEDIUM_DUTY_WRECKER',
  'HEAVY_DUTY_WRECKER',
  'ROTATOR',
  'INTEGRATED_WRECKER',
  'HEAVY_FLATBED',
];

/**
 * How a usage charge is measured. The server rejects anything else, so this is
 * the whole vocabulary rather than a starting point.
 */
export const CHARGE_BASES = ['FLAT', 'PER_HOUR'] as const;
export type ChargeBasis = (typeof CHARGE_BASES)[number];

export interface TowProviderEquipment {
  providerEquipmentId?: number | null;
  towProviderId: number;
  equipmentType: string;
  /** Required when equipmentType is OTHER. */
  otherLabel: string | null;
  quantity: number;
  capacityTons: number | null;
  make: string | null;
  model: string | null;
  year: number | null;
  notes: string | null;
  active: boolean;

  /**
   * "Is there an extra charge when this is used?" — three states, not a
   * boolean with a default. `null` means nobody has answered and a job needing
   * this equipment cannot be priced, so the provider is skipped; `false` means
   * they answered no and it prices as zero. Never send `false` for unanswered.
   */
  chargeApplies: boolean | null;
  /** Required when `chargeApplies` is true. Zero is allowed and means zero. */
  chargeAmount: number | null;
  /** Required when `chargeApplies` is true; the server validates the code. */
  chargeBasis: ChargeBasis | null;
}

export interface EquipmentCoverage {
  equipmentType: string;
  /** Distinct providers with at least one in service. */
  providerCount: number;
  /** Total units across the network. */
  unitCount: number;
  maxCapacityTons: number | null;
}

export interface ProviderEquipmentSummary {
  towProviderId: number;
  companyName: string;
  unitCount: number;
  typeCount: number;
  maxCapacityTons: number | null;
  /** Rotator or air cushions — can they upright an overturned rig. */
  hasHeavyRecovery: boolean;
  equipment: TowProviderEquipment[];
}

export interface EquipmentOverview {
  providerCount: number;
  providersWithNoEquipment: number;
  totalUnits: number;
  coverage: EquipmentCoverage[];
  providers: ProviderEquipmentSummary[];
}

/**
 * GET /api/v1/TowProviderEquipment/get-equipment
 *
 * `towProviderId` is read only for administrators; a provider's own comes from
 * their token.
 */
export async function getEquipment(towProviderId?: number): Promise<TowProviderEquipment[]> {
  const response = await apiClient.get<ApiResponse<TowProviderEquipment[]>>(
    '/api/v1/TowProviderEquipment/get-equipment',
    { params: towProviderId === undefined ? undefined : { towProviderId } },
  );
  return response.data.data ?? [];
}

/** PUT save-equipment — creates when `providerEquipmentId` is absent. */
export async function saveEquipment(item: TowProviderEquipment): Promise<number> {
  const response = await apiClient.put<ApiResponse<number>>(
    '/api/v1/TowProviderEquipment/save-equipment',
    item,
  );
  return response.data.data;
}

export async function deleteEquipment(providerEquipmentId: number): Promise<boolean> {
  const response = await apiClient.delete<ApiResponse<boolean>>(
    '/api/v1/TowProviderEquipment/delete-equipment',
    { params: { providerEquipmentId } },
  );
  return response.data.data;
}

/**
 * GET equipment-overview — what the network owns, by capability and provider.
 * Administrators only. Counts exclude out-of-service units: the question is
 * what can be dispatched today.
 */
export async function getEquipmentOverview(): Promise<EquipmentOverview> {
  const response = await apiClient.get<ApiResponse<EquipmentOverview>>(
    '/api/v1/TowProviderEquipment/equipment-overview',
  );
  return response.data.data;
}
